import AvatarWithMarketplace from "@components/AvatarWithMarketplace";
import Box from '@components/Box';
import CancelBtn from "../components/CancelBtn";
import DataTable from "@containers/DataTable";
import InfoIcon from '@assets/icons/info-grey-18.svg';
import NoItems from "@components/NoItems";
import ParcelModalWindow from '@containers/ParcelModalWindow';
import Popup from "@components/Popup";
import TableSelectionPanel from '@components/Table/TableSelectionPanel';
import TrackingNumber from "@components/TrackingNumber";
import { AddSellerPanel } from "@components/AddSeller";
import { Button, ButtonSize } from "@components/Button";
import { FilterModel } from "@hooks/useFiltersSearchParams";
import { formatDate } from "@utils/formatValues";
import { getResalesPage, ResaleDto, ResaleItemDto } from '@api/apiClient';
import { Modal } from "@components/Modal";
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Tooltip } from '@components/new/Tooltip';
import { useState, ReactElement } from 'react';
import { useTranslation } from "react-i18next";

const ResaleTab = ({
  filters,
  pending,
  downloadingBtn,
  handlerSetSavedResaleSelection,
  savedResaleSelection,
  refreshData,
  sellersNotFound,
  noData,
}: {
  filters: FilterModel[],
  pending: boolean,
  downloadingBtn: ReactElement,
  handlerSetSavedResaleSelection: (value: (((prevState: string[]) => string[]) | string[])) => void,
  savedResaleSelection: string[],
  refreshData: () => void,
  sellersNotFound: boolean,
  noData: boolean,
}) => {
  const [detailId, setDetailId] = useState<string>('');
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState<boolean>(false);

  const { t } = useTranslation();

  const columns: TableColumn<ResaleDto>[] = [
    {
      header: (
        <Box flex="row">
          {t("common.trackingNumber")}
          {" "}
          <Tooltip
            action={<img alt="Info" src={InfoIcon} />}
            position="top"
            text={(
              <Box flex="column">
                <span>{t("resaleTable.tooltip-1")}</span>
                <span>{t("resaleTable.tooltip-2")}</span>
              </Box>
            )}
            whiteSpace="pre-wrap"
          />
        </Box>
      ),
      accessor: (row: TableRowType<ResaleDto>) => (
          <TrackingNumber
              externalId={row.data?.oldTrackNumber}
              ignoreClickSecond
              onClick={() => {
                setDetailId(row.data?.newId);
                setIsOpenParcelWindow(true);
              }}
              trackNumber={row.data?.newTrackNumber}
          />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t("resaleTable.itemName"),
      accessor: (row: TableRowType<ResaleDto>) => row.data.items?.[0]?.name ?? "",
      width: "235px"
    },
    {
      header: t("resaleTable.quantity"),
      accessor: (row: TableRowType<ResaleDto>) => row.data.items.length ? row.data.items.reduce((accumulator: number, item: ResaleItemDto) => accumulator + item.quantity, 0) : '',
      alignment: "left",
      type: TableColumnType.TERTIARY
    },
    {
      header: t("resaleTable.service"),
      accessor: (row: TableRowType<ResaleDto>) => row.data.serviceName,
      width: "200px"
    },
    {
      header: t("resaleTable.seller"),
      accessor: (row: TableRowType<ResaleDto>) => (
        <AvatarWithMarketplace
          id={row.data.sellerId}
          name={row.data.sellerName}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t("resaleTable.setupAt"),
      accessor: (row: TableRowType<ResaleDto>) => formatDate(row.data.setupAt, "dd.MM.yy HH:mm"),
      type: TableColumnType.TERTIARY
    },
    {
      header: "",
      accessor: (row: TableRowType<ResaleDto>) => (
        <>
          {row.data.isCancelable ? (
            <CancelBtn
              id={row.data.id}
              estimatedUtilizationDate={row.data.estimatedUtilizationDate ?? ""}
              tabType="resale"
              handleRefresh={() => {
                handlerSetSavedResaleSelection([]);
                refreshData();
              }}
            />
          ) : (
            <Popup text={t("disposalTable.resetButtonNoty")}>
              <Button
                disabled
                onClick={() => {}}
                size={ButtonSize.XS}
                isRounded
                title={t("disposalTable.resetButton")}
              />
            </Popup>
          )}
        </>
      ),
      alignment: 'center',
      noOverflow: true
    },
  ];

  const closeParcelWindow = () => {
    setIsOpenParcelWindow(false);
  };

  if (sellersNotFound) {
    return (
      <AddSellerPanel isFirstSeller />
    );
  }

  if (noData) {
    <NoItems
      title={t("noItems.returns.title")}
      description={t("noItems.returns.description")}
    />
  }

  return (
    <TableSelectionPanel
        isShown={savedResaleSelection.length > 0}
        items={[
          {
            label: t("awaitingTableSelectionPanel.selected"),
            value: savedResaleSelection.length,
          }
        ]}
        buttons={[downloadingBtn]}
    >
      <DataTable<ResaleDto>
        queryKey="resales-page"
        pending={pending}
        getData={getResalesPage}
        columns={columns}
        selectable
        selectedIds={savedResaleSelection}
        onSelectionChange={handlerSetSavedResaleSelection}
        notFound={t("resaleTab.notResale")}
        filters={filters}
      />

      <Modal
          bodyWithoutIndentations
          isOpen={isOpenParcelWindow}
          onClickOverlay={closeParcelWindow}
          onClose={closeParcelWindow}
          width={900}
      >
        <ParcelModalWindow parcelId={detailId}/>
      </Modal>
    </TableSelectionPanel>
  );
};

export default ResaleTab;
